import { createEmptyHistoryState, registerHistory } from '@lexical/history';
import { registerPlainText } from '@lexical/plain-text';
import { cn } from '@newfront-insurance/core-ui/v2';
import {
  $createLineBreakNode,
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_BEFORE_HIGH,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_UP_COMMAND,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  KEY_MODIFIER_COMMAND,
  KEY_TAB_COMMAND,
  PASTE_COMMAND,
  TextNode,
  UNDO_COMMAND,
  createEditor,
  type LexicalEditor,
  mergeRegister,
} from 'lexical';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  type ClipboardEvent,
  type RefObject,
} from 'react';

import { $createSkillCommandNode, $isSkillCommandNode, SkillCommandNode } from './prompt-editor-nodes';
import {
  getHandleFromCommandEntityText,
  getSkillCommandEntityMatch,
  splitPromptTextLines,
} from './prompt-editor-utils';
import type { SkillCommand } from '../../../../lib/skill-constants';

export interface PromptEditorHandle {
  focus: () => void;
  getCursorPosition: () => number;
  getSelectedSkillId: () => string | undefined;
  setPlainText: (text: string, cursorPosition?: number) => void;
  setSkillCommand: (text: string, selection: SelectedSkillCommand, cursorPosition?: number) => void;
}

export interface SelectedSkillCommand {
  skillId: string;
  startIndex: number;
  endIndex: number;
}

interface PromptEditorProps {
  id?: string;
  ariaLabel: string;
  value: string;
  commands: SkillCommand[];
  placeholder: string;
  className?: string;
  editorRef: RefObject<HTMLDivElement | HTMLTextAreaElement | null>;
  disabled?: boolean;
  submitOnEnter?: boolean;
  isCommandMenuOpen: boolean;
  canSubmit: boolean;
  onChange: (value: string, cursorPosition: number) => void;
  onCommandMenuClose: () => void;
  onCommandMenuNavigate: (delta: number) => void;
  onCommandMenuSelect: () => void;
  onPaste: (event: ClipboardEvent<HTMLDivElement>) => void;
  onSubmit: () => void;
}

const editorTheme = {
  paragraph: 'm-0',
};

const INSTRUCTION_PLACEHOLDER_CLASSES = [
  'after:pointer-events-none',
  'after:ml-1',
  'after:text-steel-300',
  'after:select-none',
  'after:content-[attr(data-skill-instruction-placeholder)]',
];

export const PromptEditor = forwardRef<PromptEditorHandle, PromptEditorProps>(function PromptEditor(
  {
    id,
    ariaLabel,
    value,
    commands,
    placeholder,
    className,
    editorRef,
    disabled = false,
    submitOnEnter = true,
    isCommandMenuOpen,
    canSubmit,
    onChange,
    onCommandMenuClose,
    onCommandMenuNavigate,
    onCommandMenuSelect,
    onPaste,
    onSubmit,
  },
  ref,
) {
  const initialValueRef = useRef(value);
  const editor = useMemo(
    () =>
      createEditor({
        namespace: 'NovaPromptEditor',
        theme: editorTheme,
        nodes: [SkillCommandNode],
        onError(error: Error) {
          throw error;
        },
      }),
    [],
  );
  const historyState = useMemo(() => createEmptyHistoryState(), []);

  const setPlainText = useCallback(
    (text: string, cursorPosition?: number) => {
      setEditorPlainText(editor, text, cursorPosition);
    },
    [editor],
  );
  const setSkillCommand = useCallback(
    (text: string, selection: SelectedSkillCommand, cursorPosition?: number) => {
      setEditorPlainText(editor, text, cursorPosition, false, selection);
    },
    [editor],
  );
  const getSelectedSkillId = useCallback(() => readSelectedSkillId(editor), [editor]);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        editor.focus();
      },
      getCursorPosition: () => getDOMCursorPosition(editorRef.current),
      getSelectedSkillId,
      setPlainText,
      setSkillCommand,
    }),
    [editor, editorRef, getSelectedSkillId, setPlainText, setSkillCommand],
  );

  useEffect(() => {
    const rootElement = editorRef.current as HTMLDivElement | null;
    editor.setRootElement(rootElement);
    setEditorPlainText(editor, initialValueRef.current, initialValueRef.current.length, true);
    editor.focus(undefined, { defaultSelection: 'rootEnd' });

    return () => {
      editor.setRootElement(null);
    };
  }, [editor, editorRef]);

  useEffect(() => {
    historyState.current = { editor, editorState: editor.getEditorState() };
    historyState.undoStack = [];
    historyState.redoStack = [];
    return registerHistory(editor, historyState, 1000);
  }, [editor, historyState]);

  useEffect(() => {
    let currentValue = '';
    editor.getEditorState().read(() => {
      currentValue = $getRoot().getTextContent();
    });

    if (currentValue !== value) {
      setEditorPlainText(editor, value, value.length);
    }
  }, [editor, value]);

  useEffect(
    () =>
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          const nextValue = $getRoot().getTextContent();
          onChange(nextValue, getDOMCursorPosition(editorRef.current));
        });
      }),
    [editor, editorRef, onChange],
  );

  useEffect(() => registerPlainText(editor), [editor]);

  useEffect(() => editor.registerCommand(PASTE_COMMAND, () => true, COMMAND_PRIORITY_HIGH), [editor]);

  useEffect(() => registerSkillCommandTransforms(editor, commands), [commands, editor]);

  useEffect(() => registerInstructionPlaceholder(editor, commands), [commands, editor]);

  useEffect(
    () =>
      registerPromptKeyboardCommands({
        editor,
        submitOnEnter,
        isCommandMenuOpen,
        canSubmit,
        onCommandMenuClose,
        onCommandMenuNavigate,
        onCommandMenuSelect,
        onSubmit,
      }),
    [
      canSubmit,
      editor,
      isCommandMenuOpen,
      onCommandMenuClose,
      onCommandMenuNavigate,
      onCommandMenuSelect,
      onSubmit,
      submitOnEnter,
    ],
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLDivElement>) => {
      onPaste(event);

      const text = event.clipboardData.getData('text/plain');
      const fileCount = event.clipboardData.files?.length ?? 0;
      if (!text && fileCount === 0) {
        return;
      }

      event.preventDefault();

      if (!text) {
        return;
      }

      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          selection.insertRawText(text);
        }
      });
    },
    [editor, onPaste],
  );

  return (
    <div className="relative w-full">
      {value.length === 0 ? (
        <div className="text-muted-foreground pointer-events-none absolute top-2.5 left-3 text-base select-none">
          {placeholder}
        </div>
      ) : null}
      <div
        id={id}
        ref={editorRef as RefObject<HTMLDivElement>}
        role="textbox"
        aria-label={ariaLabel}
        aria-multiline="true"
        aria-placeholder={placeholder}
        aria-disabled={disabled}
        contentEditable={!disabled}
        tabIndex={disabled ? -1 : 0}
        suppressContentEditableWarning
        spellCheck
        data-testid="chat-input"
        className={cn(
          'max-h-[calc(25dvh)] min-h-[24px] resize-none overflow-y-auto',
          'bg-background !text-base transition-colors',
          'min-h-[92px] border-none focus-within:border-none hover:focus-within:border-none',
          'cursor-text rounded-none px-3 py-2.5',
          'focus:ring-0 focus:outline-none',
          'break-words whitespace-pre-wrap',
          className,
        )}
        onPaste={handlePaste}
      />
    </div>
  );
});

function registerPromptKeyboardCommands({
  editor,
  submitOnEnter,
  isCommandMenuOpen,
  canSubmit,
  onCommandMenuClose,
  onCommandMenuNavigate,
  onCommandMenuSelect,
  onSubmit,
}: {
  editor: LexicalEditor;
  submitOnEnter: boolean;
  isCommandMenuOpen: boolean;
  canSubmit: boolean;
  onCommandMenuClose: () => void;
  onCommandMenuNavigate: (delta: number) => void;
  onCommandMenuSelect: () => void;
  onSubmit: () => void;
}) {
  return mergeRegister(
    editor.registerCommand(
      KEY_MODIFIER_COMMAND,
      (event) => {
        if (
          event.defaultPrevented ||
          event.altKey ||
          event.shiftKey ||
          event.key.toLowerCase() !== 'z' ||
          (!event.ctrlKey && !event.metaKey)
        ) {
          return false;
        }

        event.preventDefault();
        return editor.dispatchCommand(UNDO_COMMAND, undefined);
      },
      COMMAND_PRIORITY_EDITOR,
    ),
    editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        if (event?.isComposing) {
          return false;
        }

        if (isCommandMenuOpen) {
          event?.preventDefault();
          onCommandMenuSelect();
          return true;
        }

        event?.preventDefault();

        if (!submitOnEnter || event?.shiftKey) {
          editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              selection.insertLineBreak();
            }
          });
          return true;
        }

        if (canSubmit) {
          onSubmit();
        }

        return true;
      },
      COMMAND_PRIORITY_BEFORE_HIGH,
    ),
    editor.registerCommand(
      KEY_ESCAPE_COMMAND,
      (event) => {
        if (!isCommandMenuOpen) {
          return false;
        }

        event?.preventDefault();
        event?.stopPropagation();
        onCommandMenuClose();
        return true;
      },
      COMMAND_PRIORITY_BEFORE_HIGH,
    ),
    editor.registerCommand(
      KEY_ARROW_DOWN_COMMAND,
      (event) => {
        if (!isCommandMenuOpen) {
          return false;
        }

        event?.preventDefault();
        onCommandMenuNavigate(1);
        return true;
      },
      COMMAND_PRIORITY_BEFORE_HIGH,
    ),
    editor.registerCommand(
      KEY_ARROW_UP_COMMAND,
      (event) => {
        if (!isCommandMenuOpen) {
          return false;
        }

        event?.preventDefault();
        onCommandMenuNavigate(-1);
        return true;
      },
      COMMAND_PRIORITY_BEFORE_HIGH,
    ),
    editor.registerCommand(
      KEY_TAB_COMMAND,
      (event) => {
        if (!isCommandMenuOpen) {
          return false;
        }

        event?.preventDefault();
        onCommandMenuSelect();
        return true;
      },
      COMMAND_PRIORITY_BEFORE_HIGH,
    ),
  );
}

function registerSkillCommandTransforms(editor: LexicalEditor, commands: SkillCommand[]) {
  const getMatch = (text: string) => getSkillCommandEntityMatch(text, commands);

  return mergeRegister(
    editor.registerNodeTransform(TextNode, (textNode) => {
      if ($isSkillCommandNode(textNode)) {
        return;
      }

      const text = textNode.getTextContent();
      const match = getMatch(text);
      if (!match) {
        return;
      }

      $replaceTextNodeRange(textNode, match.start, match.end);
    }),
    editor.registerNodeTransform(SkillCommandNode, (node) => {
      const text = node.getTextContent();
      const match = getMatch(text);
      if (match?.start === 0 && match.end === text.length) {
        const selectedSkillId = node.getSkillId();
        const selectedCommand = selectedSkillId
          ? commands.find(({ skill }) => skill?.id === selectedSkillId)
          : undefined;
        if (selectedSkillId && selectedCommand?.handle !== getHandleFromCommandEntityText(text)) {
          node.replace($createSkillCommandNode(text));
        }
        return;
      }

      node.replace($createTextNode(text));
    }),
  );
}

function registerInstructionPlaceholder(editor: LexicalEditor, commands: SkillCommand[]) {
  const updateInstructionPlaceholder = (editorState = editor.getEditorState()) => {
    let targetNodeKey: string | null = null;
    let instructionPlaceholder: string | undefined;

    editorState.read(() => {
      const rootText = $getRoot().getTextContent();
      const commandNode = $getRoot().getAllTextNodes().find($isSkillCommandNode);
      if (!commandNode) {
        return;
      }

      const commandText = commandNode.getTextContent();
      const selectedSkillId = commandNode.getSkillId();
      const command = selectedSkillId
        ? commands.find(({ skill }) => skill?.id === selectedSkillId)
        : commands.find(({ handle }) => handle === commandNode.getHandle());
      if (
        !rootText.startsWith(commandText) ||
        rootText.slice(commandText.length).trim().length > 0 ||
        !command?.instructionPlaceholder?.trim()
      ) {
        return;
      }

      const nextSibling = commandNode.getNextSibling();
      targetNodeKey = $isTextNode(nextSibling) ? nextSibling.getKey() : commandNode.getKey();
      instructionPlaceholder = command.instructionPlaceholder;
    });

    const rootElement = editor.getRootElement();
    rootElement?.querySelectorAll<HTMLElement>('[data-skill-instruction-placeholder]').forEach((element) => {
      delete element.dataset.skillInstructionPlaceholder;
      element.classList.remove(...INSTRUCTION_PLACEHOLDER_CLASSES);
    });

    if (!targetNodeKey || !instructionPlaceholder) {
      return;
    }

    const targetElement = editor.getElementByKey(targetNodeKey);
    if (targetElement) {
      targetElement.dataset.skillInstructionPlaceholder = instructionPlaceholder;
      targetElement.classList.add(...INSTRUCTION_PLACEHOLDER_CLASSES);
    }
  };

  updateInstructionPlaceholder();
  return editor.registerUpdateListener(({ editorState }) => updateInstructionPlaceholder(editorState));
}

function $replaceTextNodeRange(textNode: TextNode, startOffset: number, endOffset: number) {
  const targetNodes = startOffset === 0 ? textNode.splitText(endOffset) : textNode.splitText(startOffset, endOffset);
  const targetNode = startOffset === 0 ? targetNodes[0] : targetNodes[1];

  if (!targetNode) {
    return;
  }

  const text = targetNode.getTextContent();
  if (text.length === 0) {
    return;
  }

  targetNode.replace($createSkillCommandNode(text));
}

function setEditorPlainText(
  editor: LexicalEditor,
  text: string,
  cursorPosition = text.length,
  discrete = false,
  selectedSkillCommand?: SelectedSkillCommand,
) {
  editor.update(
    () => {
      const root = $getRoot();
      const paragraph = $createParagraphNode();
      const selectedCommandText = selectedSkillCommand
        ? text.slice(selectedSkillCommand.startIndex, selectedSkillCommand.endIndex)
        : '';
      const hasValidSelectedCommand =
        selectedSkillCommand &&
        selectedSkillCommand.startIndex >= 0 &&
        selectedSkillCommand.endIndex <= text.length &&
        selectedSkillCommand.startIndex < selectedSkillCommand.endIndex &&
        selectedCommandText.startsWith('/') &&
        !/[\r\n]/.test(selectedCommandText);

      if (hasValidSelectedCommand) {
        $appendPlainText(paragraph, text.slice(0, selectedSkillCommand.startIndex));
        paragraph.append($createSkillCommandNode(selectedCommandText, selectedSkillCommand.skillId));
        $appendPlainText(paragraph, text.slice(selectedSkillCommand.endIndex));
      } else {
        $appendPlainText(paragraph, text);
      }

      root.clear();
      root.append(paragraph);
      $selectPlainTextOffset(cursorPosition);
    },
    discrete ? { discrete: true } : undefined,
  );
}

function $appendPlainText(paragraph: ReturnType<typeof $createParagraphNode>, text: string) {
  splitPromptTextLines(text).forEach((line, index) => {
    if (index > 0) {
      paragraph.append($createLineBreakNode());
    }

    if (line.length > 0) {
      paragraph.append($createTextNode(line));
    }
  });
}

function readSelectedSkillId(editor: LexicalEditor): string | undefined {
  let selectedSkillId: string | undefined;
  editor.getEditorState().read(() => {
    selectedSkillId = $getRoot()
      .getAllTextNodes()
      .find((node): node is SkillCommandNode => $isSkillCommandNode(node) && Boolean(node.getSkillId()))
      ?.getSkillId();
  });
  return selectedSkillId;
}

function $selectPlainTextOffset(cursorPosition: number) {
  let remainingOffset = Math.max(0, cursorPosition);
  const textNodes = $getRoot().getAllTextNodes();

  for (const textNode of textNodes) {
    const textLength = textNode.getTextContent().length;

    if (remainingOffset <= textLength) {
      textNode.select(remainingOffset, remainingOffset);
      return;
    }

    remainingOffset -= textLength;
  }

  $getRoot().selectEnd();
}

function getDOMCursorPosition(element: HTMLElement | null): number {
  const selection = window.getSelection();
  if (!element || !selection || selection.rangeCount === 0) {
    return 0;
  }

  const range = selection.getRangeAt(0);
  if (!element.contains(range.endContainer)) {
    return element.textContent?.length ?? 0;
  }

  const preCaretRange = range.cloneRange();
  preCaretRange.selectNodeContents(element);
  preCaretRange.setEnd(range.endContainer, range.endOffset);

  return preCaretRange.toString().length;
}
